import React from "react";
import { Button } from "../ui/button";
import { CONSOLE_RUN_TIMEZONE_UNSET_NOTE_LIST, isConsoleRunTimezoneUnset } from "../../utils/dateTimeUtils";
import { getConsoleRunUrl } from "../../utils/consoleUtils";
import {
  EFFORT_CLASS,
  EFFORT_LABEL,
  REL_DOT,
  REL_LABEL,
  RUN_TYPES,
  STATUS_BADGE_VARIANTS,
  TYPE_CLASS,
  TYPE_LABEL,
  WORKFLOW_LABEL,
} from "../../utils/consoleRunsConstants";
import { getRunCompletenessWithFields, runPublishGate } from "../../utils/consoleRunsMeetingModel";
import { ConsolePageHeader } from "./ConsoleShared";

/**
 * Console Runs list: filters, table, pagination. Data derivation stays in RunsView.
 */
export default function ConsoleRunsListPanel({
  navigate,
  cityId,
  meetingsRange,
  regions,
  search,
  setSearch,
  filterSlot,
  setFilterSlot,
  filterType,
  setFilterType,
  filterEffort,
  setFilterEffort,
  filterReliability,
  setFilterReliability,
  filterRegion,
  setFilterRegion,
  runs,
  totalFiltered,
  paginatedRuns,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalPages,
  clubById,
  handleSort,
  openAddRunModal,
  openEditRunModal,
  handleDeleteRunClick,
  deleteRunSubmitting,
  onRegeocodeMissingCoords,
  regeocodeBusy,
}) {
  return (
    <div className="console-main-inner">
      <ConsolePageHeader
        label="City Seeding"
        title="Runs"
        sub="Recurring runs in this city: each series is one row (schedule shows the next occurrence in this window). Date window uses Schedule window in the bar. Times use each run's IANA timezone when set; otherwise UTC. Filter by slot, type, effort. Live API."
      />
      <div className="filter-bar runs-toolbar">
        <input
          type="text"
          placeholder="Search runs, clubs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="filter-sel runs-toolbar-search"
          style={{ minWidth: 180 }}
          aria-label="Search by club name, run name, location, or notes"
        />
        <select value={filterSlot} onChange={(e) => setFilterSlot(e.target.value)} aria-label="Filter by slot" className="filter-sel">
          <option value="">AM & PM</option>
          <option value="am">AM only</option>
          <option value="pm">PM only</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} aria-label="Filter by type" className="filter-sel">
          <option value="">All types</option>
          {RUN_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select value={filterEffort} onChange={(e) => setFilterEffort(e.target.value)} aria-label="Filter by effort" className="filter-sel">
          <option value="">All efforts</option>
          <option value="relaxed">Relaxed</option>
          <option value="moderate">Moderate</option>
          <option value="challenging">Challenging</option>
        </select>
        <select
          value={filterReliability}
          onChange={(e) => setFilterReliability(e.target.value)}
          aria-label="Filter by reliability"
          className="filter-sel"
        >
          <option value="">All reliability</option>
          <option value="anchor">⚓ Anchor</option>
          <option value="regular">✓ Regular</option>
          <option value="occasional">~ Occasional</option>
          <option value="unverified">? Unverified</option>
        </select>
        <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} aria-label="Filter by region" className="filter-sel">
          <option value="">All regions</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <span id="runs-count" style={{ fontSize: 11, color: "var(--console-muted)" }}>
          {totalFiltered === runs.length ? `${runs.length} runs` : `${totalFiltered} of ${runs.length} (filtered)`}
        </span>
        {typeof onRegeocodeMissingCoords === "function" && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-[var(--console-border)] text-[var(--console-ink)] shrink-0"
            disabled={Boolean(regeocodeBusy)}
            title="Fill lat/lon/map location for runs that have an address but no coordinates (uses geocoder; may take a minute)"
            onClick={() => onRegeocodeMissingCoords()}
          >
            {regeocodeBusy ? "Geocoding…" : "Fix missing coordinates"}
          </Button>
        )}
        <Button size="sm" className="bg-[#1A1916] hover:bg-[#3C3830] text-white" onClick={openAddRunModal}>
          + Add Run
        </Button>
      </div>
      <div className="tbl-wrap runs-table-wrap">
        <table className="rc-table run-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("club_id")} title="The club that owns this run. Click to sort.">
                Club
              </th>
              <th onClick={() => handleSort("runName")} title="The name of this recurring run as it appears in the app.">
                Run
              </th>
              <th
                onClick={() => handleSort("day")}
                title="Day, slot, and time use each run's IANA timezone when set; otherwise UTC (rows without a timezone note UTC)."
              >
                Schedule
              </th>
              <th
                onClick={() => handleSort("runType")}
                title="Easy/Social · Long Run · Workout/Track · General · Beginner. Affects how the run appears in discovery."
              >
                Type
              </th>
              <th
                onClick={() => handleSort("effortLevel")}
                title="Relaxed = all paces welcome. Moderate = some fitness needed. Challenging = performance-oriented."
              >
                Effort
              </th>
              <th
                onClick={() => handleSort("reliability")}
                title="Reliability weighting affects coverage scoring. Anchor = weight 1.0; Regular = 1.0; Occasional = 0.5; Unverified = 0.25."
              >
                Reliability
              </th>
              <th>Status</th>
              <th onClick={() => handleSort("startLocation")} title="The verified meet-up address for this run. Should match what's in the app.">
                Location
              </th>
              <th title="How complete this run record is (four fields, 25% each). Under 100%, the row lists what is still missing.">
                Completeness
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {paginatedRuns.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: 18, color: "var(--console-muted)", fontSize: 12 }}>
                  {runs.length === 0 ? "No runs found." : "No runs match filters."}
                </td>
              </tr>
            ) : (
              paginatedRuns.map((r) => {
                const club = clubById[r.club_id];
                const { pct: rc, missingLabels } = getRunCompletenessWithFields(r);
                const compCls = rc >= 75 ? "comp-ok" : rc >= 45 ? "comp-partial" : "comp-low";
                const missingSummary =
                  rc < 100 && missingLabels.length > 0 ? `Missing for 100%: ${missingLabels.join("; ")}` : "";
                const rel = r.reliability;
                const status = r.status != null && String(r.status).trim() !== "" ? r.status : null;
                const statusLabel = status
                  ? WORKFLOW_LABEL[status] ?? (String(status).charAt(0).toUpperCase() + String(status).slice(1))
                  : "—";
                const statusBadgeClass =
                  status && STATUS_BADGE_VARIANTS.has(String(status)) ? `ps-badge ps-${status}` : "ps-badge";
                const typeClass = TYPE_CLASS[r.runType];
                const typeLabel = TYPE_LABEL[r.runType];
                const effortClass = EFFORT_CLASS[r.effortLevel];
                const effortLabel = EFFORT_LABEL[r.effortLevel];
                const locationDisplay = r.startLocation != null ? String(r.startLocation).trim() : "";
                return (
                  <tr
                    key={r.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(getConsoleRunUrl(cityId, r.id, meetingsRange))}
                    onKeyDown={(e) =>
                      e.key === "Enter" && (navigate(getConsoleRunUrl(cityId, r.id, meetingsRange)), e.preventDefault())
                    }
                    className="runs-table-row-clickable"
                  >
                    <td>
                      <span className="td-club-name">{club?.name ?? r.club_name}</span>
                      {club?.region && <span className="td-sub">{club.region}</span>}
                    </td>
                    <td>{r.runName}</td>
                    <td>
                      {r.day}{" "}
                      {r.slot && <span style={{ color: "var(--console-muted)" }}>{r.slot.toUpperCase()}</span>}
                      <span className="td-sub">
                        {[r.time != null && String(r.time).trim() !== "" ? r.time : null, r.recurrence ?? null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                      {isConsoleRunTimezoneUnset(r) && (
                        <span className="td-sub" style={{ display: "block", marginTop: 2, color: "var(--console-muted)" }}>
                          {CONSOLE_RUN_TIMEZONE_UNSET_NOTE_LIST}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={typeClass ? `spill ${typeClass}` : "spill"}>{typeLabel}</span>
                    </td>
                    <td>
                      <span className={effortClass ? `spill ${effortClass}` : "spill"}>{effortLabel}</span>
                    </td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--console-ink)" }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: REL_DOT[rel],
                            flexShrink: 0,
                            boxShadow: REL_DOT[rel] ? `0 0 0 2px ${REL_DOT[rel]}22` : undefined,
                          }}
                        />
                        {REL_LABEL[rel] ?? rel}
                      </span>
                    </td>
                    <td>
                      <span className={statusBadgeClass}>{statusLabel}</span>
                      {r.workflowStatus !== "published" &&
                        (() => {
                          const errs = runPublishGate(r, clubById);
                          return errs.length > 0 ? (
                            <div style={{ fontSize: 9, color: "var(--console-red)", marginTop: 3, lineHeight: 1.6 }}>
                              {errs.map((e, i) => (
                                <div key={i}>✗ {e}</div>
                              ))}
                            </div>
                          ) : null;
                        })()}
                    </td>
                    <td
                      style={{ maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 }}
                      title={locationDisplay || undefined}
                    >
                      {locationDisplay || "—"}
                    </td>
                    <td style={{ verticalAlign: "top" }}>
                      <span className={`comp-badge ${compCls}`} title={missingSummary || undefined}>
                        {rc}%
                      </span>
                      {rc < 100 && missingLabels.length > 0 ? (
                        <div className="run-table-completeness-missing" title={missingSummary}>
                          {missingLabels.map((label) => (
                            <div key={label}>· {label}</div>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="td-act" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="td-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditRunModal(r);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="td-btn del"
                          disabled={deleteRunSubmitting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRunClick(r);
                          }}
                        >
                          Del
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalFiltered > 0 && (
        <div
          className="runs-pagination"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--console-border)",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--console-muted)" }}>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Rows per page"
            className="filter-sel"
            style={{ width: 70 }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: 12, color: "var(--console-ink)", marginLeft: 8 }}>
            {totalFiltered === 0
              ? "0 runs"
              : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalFiltered)} of ${totalFiltered}`}
          </span>
          <div style={{ flex: 1 }} />
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="tb-btn"
            style={{ minWidth: 70 }}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="tb-btn"
            style={{ minWidth: 70 }}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
